import { ChevronLeftIcon, ExternalLink, Plus } from "lucide-react";
import { Link, useRoute, useSearchParams } from "wouter";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { useProducts, useCart, InstrumentRow } from "@/features/marketplace";
import type { Collection } from "@/types/api";
import useMusioApi from "@/contexts/client/musio-client-context";
import { createInCartHandler } from "@/utils/utils";
import { Helmet } from "@dr.pogodin/react-helmet";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { MUS00001, MUS00003 } from "@/constants/auth-sku";

export default function CollectionPage() {
  const [, params] = useRoute('/collection/:collectionSlug');
  const collectionSlug = params?.collectionSlug;
  const { musioApi } = useMusioApi();
  const [, navigate] = useLocation();
  const [searchParams] = useSearchParams();
  const filters = searchParams.get("filters");

  const ctx = useCart();
  const { cart } = ctx;
  const { items } = useProducts();

  const item = items.find((i) => i.collectionSlug == collectionSlug);
  const [incart, setIncart] = useState(cart.includes(item!));
  const handleAddToCart = createInCartHandler(item!, incart, setIncart, ctx);

  const { data: collection, isPending, isError } = musioApi.useQuery("get", "/catalog/collection/{collectionIdOrSlug}", {
    params: {
      path: {
        collectionIdOrSlug: collectionSlug!,
      },
    },
  }, {
    retry: false,
  });

  // const { data: cartItem, isPending: isCartItemPending, isError: isCartItemError } = musioApi.useQuery("get", "/marketplace/cart-items/{id}", {
  //   params: {
  //     path: {
  //       id: collection?.collectionId,
  //     },
  //   },
  // }, {
  //   retry: false,
  // });

  if (isError) {
    console.error("Error when getting collection data");
    navigate("~/404", {
      replace: true
    });
    return;
  }

  const getCollCardImage = (coll: Collection) => {
    return coll?.assets?.card_img?.url
  }

  useEffect(() => {
    setIncart(cart.includes(item!));
  }, [cart]);

  if (isPending) {
    return (
      <div className="h-full overflow-y-auto centered">
        <LoadingIndicator />
      </div>
    );
  }

  const musioEssentials = collection?.authSkus.includes(MUS00003) || false;

  return (
    <>
      {collection &&
        <Helmet>
          <title>{`${collection?.collectionName}`} | Musio Marketplace</title>
          <meta name="description" content={collection?.collectionDescription} />
          <meta name="keywords" content={collection?.tags.join(", ")} />
        </Helmet>
      }
      <ScrollArea className="h-full overflow-y-auto">
        <div className="p-4 w-full musio-glass space-y-2 flex flex-col min-h-0">
          <div>
            <Button
              className="bg-musio-green text-musio-mid-gray text-xs size-fit over:bg-musio-green active:bg-musio-green"
              onClick={() => navigate("~/marketplace" + (filters ? `/?${decodeURIComponent(filters)}` : ""))}
            >
              <ChevronLeftIcon />
              Back
            </Button>
          </div>

          <div className="mt-4 grid md:grid-cols-[auto_auto_auto_auto] grid-rows-[auto_auto] gap-4">
            {/* section 1: col 1, rows 1-2 */}
            <div className="h-[200px] w-[200px] mx-auto md:col-start-1 md:row-start-1 md:row-span-2">
              <img
                className="w-full h-full object-cover"
                src={getCollCardImage(collection!)}
                alt={collection?.collectionName}
              />
            </div>

            {/* section 2: cols 2-3, row 1 */}
            <div className="sm:col-start-2 sm:row-start-1 md:col-start-2 md:col-span-2 md:row-start-1 flex flex-col justify-start">
              <div className="flex flex-col space-y-0.5">
                <h2 className="font-bold text-xl">{collection?.collectionName}</h2>
                <h3 className="text-musio-text-light">{collection?.collectionSubtitle}</h3>
                <Link
                  className="font-normal text-[10px] text-musio-soft-lavendar"
                  to={`~/marketplace/developer/${collection?.developerSlug ? collection?.developerSlug : collection?.developerId}`}
                >
                  By {collection?.developerName}
                </Link>
              </div>
            </div>

            {/* section 3: cols 2-3, row 2 */}
            <div className="sm:col-start-1 sm:row-start-2 md:col-start-2 md:col-span-2">
              <p className="text-musio-light-lilac">
                {collection?.collectionDescription}
              </p>
            </div>

            {/* section 4: col 4, rows 1-2 */}
            <div className="sm:col-start-2 sm:row-start-2 md:col-start-4 md:row-start-1 md:row-span-2 flex flex-col justify-start items-center space-y-2 text-musio-text-light min-w-[180px]">
              <div className="flex flex-row space-x-2 items-end">
                {musioEssentials ? (
                  <p className="text-sm italic">Included in Musio Essentials</p>
                ) : item?.cartItem?.formattedPrice ? (
                  <>
                    {item?.cartItem?.salePriceActive && (
                      <span className="relative inline-block flex-shrink-0">
                        <p className="text-xl font-normal text-lilac whitespace-nowrap">{item.cartItem.formattedPrice}</p>
                        <span
                          className="absolute left-0 top-1/2 w-full h-0.5 bg-red-500 rotate-[-10deg] pointer-events-none"
                          style={{ transform: "rotate(-10deg)", top: "50%" }}
                        />
                      </span>
                    )}
                    <p className="musio-price text-xl">{item.cartItem?.salePriceActive ? item.cartItem.formattedSalePrice : item.cartItem?.formattedPrice}</p>
                    <div
                      className={`cursor-pointer w-8 h-8 rounded-full flex items-center justify-center text-xl font-bold text-gray-500 transition-colors duration-300 ${incart ? "bg-musio-red" : "bg-musio-green"
                        }`}
                      onClick={handleAddToCart}
                    >
                      <Plus
                        className={`w-5 h-5 transition-transform duration-300 ${incart
                          ? "-rotate-45 text-musio-lilac"
                          : "rotate-0 text-musio-mid-gray"
                          }`}
                      />
                    </div>
                  </>
                ) : null}
              </div>

              {collection?.authSkus.includes(MUS00001) &&
                <>
                  {(item) && (item.cartItem?.formattedPrice && item.cartItem?.formattedPrice.length != 0) ?
                    <div className="flex items-center w-full">
                      <hr className="flex-grow border-t musio-border" />
                      <span className="mx-4 text-gray-500">or</span>
                      <hr className="flex-grow border-t musio-border" />
                    </div> : null
                  }
                  <div className="flex flex-col space-y-2 items-center justify-center">
                    <Button
                      className="
                      select-none 
                      w-[150px] 
                      h-[28px] 
                      flex 
                      items-center 
                      justify-center 
                      rounded-2xl 
                      font-bold 
                      bg-musio-blue 
                      active:bg-musio-blue/90 
                      hover:bg-musio-blue/90 
                      text-musio-light-lilac 
                      cursor-pointer
                    "
                      onClick={() => navigate("~/account/musio-plus")}
                    >
                      <p className="text-sm">Join Musio+</p>
                    </Button>
                    <div className="flex flex-row space-x-2">
                      <p className="musio-price">$9.99</p>
                      <p> / month</p>
                    </div>
                  </div>
                </>
              }
            </div>
          </div>

          <div className="text-musio-green text-xs font-medium py-1 ">
            {collection.walkthroughExternalUrl ?
              <a href={collection.walkthroughExternalUrl} target="_blank" className="cursor-pointer space-x-0.5">
                <p className="inline-block align-middle underline">Watch the full collection walkthrough on YouTube</p>
                <ExternalLink className="inline-block size-3 align-middle" />
              </a>
              : null}
          </div>

          <div className="flex flex-col flex-grow min-h-0 overflow-hidden">
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto border-t-2 border-solid border-musio-mid-gray">
              <div className="flex w-full flex-col space-y-2">
                {collection?.instruments!.map((instrument, index) => (
                  <InstrumentRow {...instrument} className="select-none" key={index} />))}
              </div>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </div>
        </div>
        <ScrollBar orientation="vertical" />
      </ScrollArea >
    </>
  );
}
